import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { LocalShipping, People } from "@mui/icons-material";
import { Box } from "@mui/material";
import Loading from "Components/Loading/Loading";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import { AnalyticsDoughnutWrapper, getChartMetricValue } from "features/analytics";
import useChartData from "features/analytics/hooks/useChartData";
import { formatNumber } from "Helpers/numbers";
import { formatPercentage, percentageOf } from "Helpers/percentage";
import { IChartData, TChartData } from "Interfaces";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useCallback, useMemo, useState } from "react";

const customersChart = {
  name: "New vs Repeat Customers",
  dimension: [ApiDimension.DIMENSION_NOT_SET],
  template: ApiChartTemplate.SNAPSHOT,
  type: ApiChartType.GRAPH,
  attributes: [],
  displayOptions: {
    showTotals: true,
    noTitle: true,
    noAttributionToggle: true,
  },
  yAxisKeys: [
    {
      key: "l",
      fields: [
        ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
        ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT,
        ApiMetric.METRIC_CUSTOMERS_COUNT,
      ],
    },
  ],
};

const orderStatusChart = {
  name: "Order Status Breakdown",
  dimension: [ApiDimension.DIMENSION_SALE_ORDER_STATUS],
  template: ApiChartTemplate.SNAPSHOT,
  type: ApiChartType.PIE,
  attributes: [],
  includeEmpty: true,
  displayOptions: {
    showTotals: true,
    pieTotalText: "Total Orders",
    noTitle: true,
    noAttributionToggle: true,
  },
  yAxisKeys: [
    {
      key: "l",
      fields: [ApiMetric.METRIC_SALES_COUNT],
    },
  ],
};

export function StoreCustomersPieChart() {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();

  const { chartData: customersData, loading: customersLoading } = useChartData(
    selectedProject,
    customersChart,
    chartOutput,
    { ignoreModel: true },
  );

  const customersPie = useMemo(() => {
    const row = customersData?.data?.[0];
    if (!row) {
      return null;
    }

    const newCustomers = Number(row[ApiMetric.METRIC_NEW_CUSTOMERS_COUNT] || 0);
    const repeatCustomers = Number(
      row[ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT] || 0,
    );
    const totalCustomers = Number(row[ApiMetric.METRIC_CUSTOMERS_COUNT] || 0);
    const valueMetric = ApiMetric.METRIC_CUSTOMERS_COUNT;

    const data: IChartData = {
      data: [
        { name: "New Customers", [valueMetric]: newCustomers },
        { name: "Repeat Customers", [valueMetric]: repeatCustomers },
      ],
      y: [{ key: "l", fields: [valueMetric] }],
    };

    return {
      chartData: data,
      totals: { name: "Total", [valueMetric]: totalCustomers },
      dataKey: valueMetric,
      repeatRate: percentageOf(repeatCustomers, totalCustomers),
    };
  }, [customersData]);

  return (
    <AnalyticsCard
      noToolbar
      ignoreAttrModel
      chart={customersChart}
      sx={{ height: "100%", minHeight: 320, width: "100%" }}
      boxProps={{ px: 2, pt: 2.5, pb: 2, height: "100%" }}
      customHeader={
        <AnalyticsCardPanelHeader
          icon={<People fontSize="small" />}
          title="New vs Repeat Customers"
          statLabel={
            customersPie
              ? `${formatPercentage(customersPie.repeatRate)} repeat`
              : undefined
          }
        />
      }
      body={
        customersLoading ? (
          <Loading fullHeight />
        ) : customersPie ? (
          <Box sx={{ height: "100%", minHeight: 0, width: "100%" }}>
            <AnalyticsDoughnutWrapper
              chartData={customersPie.chartData}
              totals={customersPie.totals}
              dataKey={customersPie.dataKey}
              pieProps={{ showTotalText: "Total Customers" }}
            />
          </Box>
        ) : null
      }
    />
  );
}

export function StoreOrderStatusPieChart() {
  const [statLabel, setStatLabel] = useState<string>();

  const onDataChange = useCallback(
    (
      rows: TChartData[],
      _comp?: TChartData[],
      meta?: { metric?: ApiMetric; totals?: TChartData },
    ) => {
      if (!rows.length) {
        setStatLabel(undefined);
        return;
      }

      const metric = meta?.metric ?? ApiMetric.METRIC_SALES_COUNT;
      const totalOrders = meta?.totals
        ? getChartMetricValue(meta.totals, metric)
        : rows.reduce(
            (sum, row) => sum + getChartMetricValue(row, metric),
            0,
          );
      const topStatus = [...rows].sort(
        (a, b) =>
          getChartMetricValue(b, metric) - getChartMetricValue(a, metric),
      )[0];

      if (!topStatus) {
        setStatLabel(
          totalOrders ? `${formatNumber(totalOrders)} orders` : undefined,
        );
        return;
      }

      const topCount = getChartMetricValue(topStatus, metric);
      const topStatusName = String(topStatus.name || "");

      setStatLabel(
        topStatusName
          ? `${formatPercentage(percentageOf(topCount, totalOrders))} ${topStatusName}`
          : `${formatNumber(totalOrders)} orders`,
      );
    },
    [],
  );

  return (
    <AnalyticsCard
      noToolbar
      ignoreAttrModel
      chart={orderStatusChart}
      onDataChange={onDataChange}
      sx={{ height: "100%", minHeight: 320, width: "100%" }}
      boxProps={{ px: 2, pt: 2.5, pb: 2, height: "100%" }}
      customHeader={
        <AnalyticsCardPanelHeader
          icon={<LocalShipping fontSize="small" />}
          title="Order Status Breakdown"
          statLabel={statLabel}
        />
      }
    />
  );
}
